import { useState, type ReactNode } from 'react';
import { PlainLink, type LinkComponent } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { FlowGlyph, FlowSpark } from './FlowGlyph';
import { FlowItemsList, type FlowItemsFilter } from './FlowItems';
import type { FlowNote, FlowStatus } from './flow-state';
import {
  causeWords,
  leadsToProblems,
  nextStep,
  noteLabel,
  placeName,
  placeRoad,
  runKind,
  unitWord,
  whereWords,
} from './flow-words';
import type { FlowCell, FlowItemsPage, FlowItemsQuery, FlowRun, FlowWhere } from './flow-types';

/** Where the detail leads: Problems, a company's page and an item's timeline, as the app routes them. */
export interface FlowLinks {
  problems: (companySlug: string) => string;
  company: (companySlug: string) => string;
  /** The document timeline for a document, unit or batch id. */
  timeline: (id: string) => string;
  Link?: LinkComponent;
  /**
   * Stop work still running or waiting (`POST /admin/work/cancel`), for staff who may (support, engineering); absent,
   * no Stop is offered. Answers how many units and documents it stopped.
   */
  stop?: (what: StopWhat, note: string) => Promise<{ units: number; documents: number }>;
}

/** What a Stop names: the batch an item belongs to, its document, or the unit itself. */
export interface StopWhat {
  kind: 'batch' | 'document' | 'unit';
  id: string;
}

function Oldest({ where, f }: { where: FlowWhere | null; f: Formatter }): ReactNode {
  if (!where) return null;
  return (
    <>
      oldest {f.duration(where.seconds)}, {whereWords(where)}
    </>
  );
}

/** The class and reason a note's items are listed by. */
export function filterOf(note: FlowNote): FlowItemsFilter {
  const reason = note.reason ?? undefined;
  if (note.kind === 'ours' || note.kind === 'theirs')
    return reason ? { class: 'parked', reason } : { class: 'parked' };
  if (note.kind === 'hold') return reason ? { class: 'held', reason } : { class: 'held' };
  if (note.kind === 'retry')
    return reason && reason !== 'retry' ? { class: 'retrying', reason } : { class: 'retrying' };
  return { class: 'waiting' };
}

const same = (a: FlowItemsFilter, b: FlowItemsFilter) =>
  a.class === b.class && a.reason === b.reason;

/** What a row needs to offer its list: the filter showing now and how to change it. */
interface Listing {
  shown: FlowItemsFilter;
  show: (filter: FlowItemsFilter) => void;
}

/** The button on a reason or a count that lists its items below. */
function Show({ filter, listing }: { filter: FlowItemsFilter; listing: Listing }): ReactNode {
  const on = same(filter, listing.shown);
  return (
    <button
      type="button"
      className={on ? 'fl-detail__show fl-detail__show--on' : 'fl-detail__show'}
      aria-pressed={on}
      onClick={() => listing.show(filter)}
    >
      {on ? 'Listed below' : 'List them'}
    </button>
  );
}

/** The way on from a reason: Problems for what is parked or slow, the company for what is held or retried. */
function NoteLink({
  note,
  where,
  links,
}: {
  note: FlowNote;
  where: FlowWhere;
  links: FlowLinks;
}): ReactNode {
  const Link = links.Link ?? PlainLink;
  const problems = leadsToProblems(note.kind);
  return (
    <Link
      className="fl-detail__link"
      href={problems ? links.problems(where.companySlug) : links.company(where.companySlug)}
    >
      {problems ? 'Open Problems' : `Open ${where.companyName}`}
    </Link>
  );
}

interface NoteRowProps {
  note: FlowNote;
  cell: FlowCell;
  links: FlowLinks;
  listing: Listing;
}

/** One reason, in full: how many, why in plain words, the oldest one's age, company and job, and what to do. */
function NoteRow({ note, cell, links, listing }: NoteRowProps): ReactNode {
  const f = useFormat();
  const where = note.cause?.oldest ?? cell.oldestWaiting;
  const companies = note.cause?.companies ?? 1;
  return (
    <li className={`fl-detail__row fl-detail__row--${note.tone}`}>
      <span className={`fl-chip fl-chip--${note.tone}`}>
        {f.int(note.count)} {noteLabel[note.kind]}
      </span>
      <span className="fl-detail__what">
        <strong>{causeWords(note.kind, note.reason)}</strong>
        {note.cause?.words ? <q className="fl-detail__words">{note.cause.words}</q> : null}
        <span className="fl-detail__where">
          <Oldest where={where} f={f} />
          {companies > 1 ? `, ${f.int(companies)} companies` : ''}
        </span>
        <span className="fl-detail__next">{nextStep(note)}</span>
      </span>
      <span className="fl-detail__ways">
        <Show filter={filterOf(note)} listing={listing} />
        {where ? <NoteLink note={note} where={where} links={links} /> : null}
      </span>
    </li>
  );
}

function RunRow({ run }: { run: FlowRun }): ReactNode {
  const f = useFormat();
  const far = run.total > 0 ? `${f.int(run.completed)} of ${f.int(run.total)}, ` : '';
  const files = run.files > 0 ? `${f.int(run.files)} ${run.files === 1 ? 'file' : 'files'}, ` : '';
  const started = run.startedAt ? `started ${f.time(run.startedAt)}, ` : '';
  const quiet = run.quietSeconds >= 60 ? `, last progress ${f.duration(run.quietSeconds)} ago` : '';
  return (
    <li className="fl-detail__row">
      <span className="fl-chip fl-chip--live">{runKind(run)}</span>
      <span className="fl-detail__what">
        <strong>
          {run.companyName}, {run.jobName}
        </strong>
        <span className="fl-detail__where">
          {files}
          {far}
          {run.stage ?? 'starting'}, {started}running {f.duration(run.runningSeconds)}
          {quiet}
        </span>
      </span>
    </li>
  );
}

interface PlainRowProps {
  count: number;
  kind: 'waiting' | 'working';
  words: string;
  where: FlowWhere | null;
  listing: Listing;
}

/** A plain count with its oldest: what can be taken now, or what is being worked. */
function PlainRow({ count, kind, words, where, listing }: PlainRowProps): ReactNode {
  const f = useFormat();
  if (count <= 0) return null;
  return (
    <li className="fl-detail__row">
      <span className={`fl-chip fl-chip--${kind === 'working' ? 'live' : 'plain'}`}>
        {f.int(count)} {kind}
      </span>
      <span className="fl-detail__what">
        <strong>
          {f.int(count)} {words}
        </strong>
        <span className="fl-detail__where">
          <Oldest where={where} f={f} />
        </span>
      </span>
      <span className="fl-detail__ways">
        <Show filter={{ class: kind }} listing={listing} />
      </span>
    </li>
  );
}

function DetailHead({
  cell,
  status,
  onClose,
}: {
  cell: FlowCell;
  status: FlowStatus;
  onClose: () => void;
}): ReactNode {
  const f = useFormat();
  return (
    <header className="fl-detail__head">
      <FlowGlyph tone={status.tone} size={18} />
      <h3 className="fl-detail__title">{placeName[cell.place]}</h3>
      <span className="fl-detail__road">{placeRoad[cell.place]}</span>
      <span className="fl-detail__rate">
        {f.int(cell.pagesPerMinute)} pages/min now, {f.int(cell.pages15m)} in the last 15 min
      </span>
      <button
        type="button"
        className="fl-detail__close"
        onClick={onClose}
        aria-label="Close the detail"
      >
        Close
      </button>
    </header>
  );
}

interface DetailRowsProps {
  cell: FlowCell;
  status: FlowStatus;
  runs: FlowRun[];
  links: FlowLinks;
  listing: Listing;
}

/** Every reason, every running reading, then the plain counts. */
function DetailRows({ cell, status, runs, links, listing }: DetailRowsProps): ReactNode {
  const unit = (n: number) => unitWord(cell.unit, n);
  const waiting = `${unit(cell.waiting)} ready to be taken`;
  const working = `${unit(cell.working)} being worked now`;
  const key = (n: FlowNote) => `${n.kind}-${n.reason ?? ''}`;
  return (
    <ul className="fl-detail__rows">
      {status.notes.map((n) => (
        <NoteRow key={key(n)} note={n} cell={cell} links={links} listing={listing} />
      ))}
      {runs.map((r) => (
        <RunRow key={r.runId} run={r} />
      ))}
      <PlainRow
        count={cell.waiting}
        kind="waiting"
        words={waiting}
        where={cell.oldestWaiting}
        listing={listing}
      />
      <PlainRow
        count={cell.working}
        kind="working"
        words={working}
        where={cell.oldestWorking}
        listing={listing}
      />
    </ul>
  );
}

export interface FlowDetailProps {
  cell: FlowCell;
  status: FlowStatus;
  runs: FlowRun[];
  links: FlowLinks;
  onClose: () => void;
  /** The company the picture is drilled to, or null for every company. */
  company: string | null;
  /** Changes when the picture does, so the open list is read again. */
  stamp: string;
  /** Reads a stage's items; without it the detail shows counts and reasons only. */
  loadItems?: ((q: FlowItemsQuery) => Promise<FlowItemsPage>) | undefined;
}

/**
 * What a stage holds, opened by clicking it: each reason in plain words with its next step, every count with its
 * oldest item, and under them the items themselves, whichever group was asked for (everything, to begin with).
 */
export function FlowDetail({
  cell,
  status,
  runs,
  links,
  onClose,
  company,
  stamp,
  loadItems,
}: FlowDetailProps): ReactNode {
  const [shown, setShown] = useState<FlowItemsFilter>({});
  const listing: Listing = { shown, show: (filter) => setShown(same(filter, shown) ? {} : filter) };
  return (
    <section
      className="fl-detail"
      aria-label={`${placeName[cell.place]} in detail`}
      data-detail={cell.place}
    >
      <DetailHead cell={cell} status={status} onClose={onClose} />
      <DetailRows cell={cell} status={status} runs={runs} links={links} listing={listing} />
      {loadItems ? (
        <FlowItemsList
          key={`${shown.class ?? ''}|${shown.reason ?? ''}`}
          place={cell.place}
          unit={cell.unit}
          filter={shown}
          company={company}
          stamp={stamp}
          load={loadItems}
          links={links}
        />
      ) : null}
      <FlowSpark
        values={cell.series}
        label={`${placeName[cell.place]}, pages a minute over 15 minutes`}
      />
    </section>
  );
}
